import { Permission } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { from, Observable } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import {
  ArticleModelName,
  GroupModelName,
  InstitutionModelName,
  InsuranceContractModelName,
  PatientModelName,
  ProductGroupModelName,
  RegionModelName,
  StandardCareProposalModelName,
  TemplateModelName,
  UsersModelName,
} from '../models/model-names';
import { AuthService } from '../services/auth.service';

@Injectable({ providedIn: 'root' })
export class PermissionValidator {
  private _isWeb: boolean;
  constructor(
    private _authService: AuthService,
    private _platform: Platform
  ) {
    this._isWeb = !this._platform.is('hybrid');
  }

  public validate(path: string): Observable<boolean> {
    return from(this._authService.init).pipe(
      filter(() => this._authService.isAuthenticated),
      switchMap(async _ => {
        if (path.includes('alberta-calendar')) {
          return await this._authService.userHasPermission('activities', Permission.Read);
        }
        if (path.includes('patient')) {
          return await this._authService.userHasPermission(PatientModelName, Permission.Read);
        }
        if (path.includes('emergency')) {
          return !this._isWeb && (await this._authService.userHasPermission('onlineSearch', Permission.Read));
        }
        if (path.includes('insurance-contract')) {
          return (
            this._isWeb && (await this._authService.userHasPermission(InsuranceContractModelName, Permission.Read))
          );
        }
        if (path.includes('article')) {
          return await this._authService.userHasPermission(ArticleModelName, Permission.Read);
        }
        if (path.includes('product-group')) {
          return this._isWeb && (await this._authService.userHasPermission(ProductGroupModelName, Permission.Read));
        }
        if (path.includes('standard-care-proposal')) {
          return await this._authService.userHasPermission(StandardCareProposalModelName, Permission.Read);
        }
        if (
          path.includes('pharmacy') ||
          path.includes('doctor') ||
          path.includes('hospital') ||
          path.includes('nursing-service') ||
          path.includes('nursing-home')
        ) {
          return await this._authService.userHasPermission(InstitutionModelName, Permission.Read);
        }
        if (path.includes('formulare')) {
          return this._isWeb && (await this._authService.userHasPermission(TemplateModelName, Permission.Read));
        }
        if (path.includes('calendar')) {
          return await this._authService.userHasPermission('calendar', Permission.Read);
        }
        if (path.includes('rights-management')) {
          return await this.hasAnyUsermanagementPermission();
        }
        if (path.includes('maintenance')) {
          return await this.userIsItLabs();
        }
        if (path.includes('agent-admin')) {
          return await this._authService.isAgent();
        }
        if (path.includes('agent-chat')) {
          return this._isWeb && (await this._authService.isAgent());
        }
        if (path.includes('list/service-task')) {
          return await this._authService.userHasPermission('serviceTaskList', Permission.Read);
        }

        return true;
      })
    );
  }

  private async hasAnyUsermanagementPermission(): Promise<boolean> {
    return (
      (await this._authService.userHasPermission(UsersModelName, Permission.Read)) ||
      (await this._authService.userHasPermission(GroupModelName, Permission.Read)) ||
      (await this._authService.userHasPermission(RegionModelName, Permission.Read))
    );
  }

  private async userIsItLabs(): Promise<boolean> {
    const tenantId = await this._authService.getCurrentUserTenantName();
    return tenantId === '1TLabs';
  }
}
